import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { usePdTwilioDevice } from '@/hooks/usePdTwilioDevice'
import { useCallbackNotifier } from '@/hooks/useCallbackNotifier'
import {
  useCampaignDetail,
  useCampaignDispositions,
  useCampaignProspects,
  useCampaignStats,
  useCreateProspectNote,
  useUpdateCampaign,
  useUpdateProspect,
  type UpdateProspectInput,
} from '@/api/powerDialer'
import { PdProspectsList } from './PdProspectsList'
import { PdProspectDetail } from './PdProspectDetail'
import { PdScriptPanel } from './PdScriptPanel'
import { PdCallBar } from './PdCallBar'
import { PdVmManagerModal } from './PdVmManagerModal'

interface Props {
  campaignId: number
  onBack: () => void
}

/**
 * Vue session active du Power Dialer pour un agent.
 * Layout : 3 colonnes + barre inférieure (parité Next).
 *
 * Pour l'instant : colonne gauche (prospects list) + placeholders centre/droite/bottom.
 * Les autres colonnes seront ajoutées dans les itérations suivantes.
 */
export function PowerDialerSessionTab({ campaignId, onBack }: Props) {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)

  // Initialise le 2e Device Twilio (subaccount Power Dialer) pendant la session.
  // Sans lui, les calls PD échouent en busy immédiat car le caller_id (numéro
  // PD subaccount) n'appartient pas au compte du Device principal (master).
  // Détruit automatiquement à l'unmount → libère la binding Twilio.
  usePdTwilioDevice({ enabled: true })

  const campaignQuery = useCampaignDetail(campaignId)
  const prospectsQuery = useCampaignProspects(campaignId)
  const statsQuery = useCampaignStats(campaignId)
  const updateCampaign = useUpdateCampaign(campaignId)

  const [activeProspectId, setActiveProspectId] = useState<number | null>(null)
  const [filter, setFilter] = useState<string>('all')
  const [selectedDispositionSlug, setSelectedDispositionSlug] =
    useState<string | null>(null)
  const [callbackDate, setCallbackDate] = useState<string | null>(null)
  const [selectedNumber, setSelectedNumber] = useState<string>('')
  const [vmManagerOpen, setVmManagerOpen] = useState(false)

  const dispositionsQuery = useCampaignDispositions(campaignId)

  const prospects = prospectsQuery.data || []

  // Programme les notifications de rappel à venir <24h pour cette session.
  useCallbackNotifier(campaignId, prospects)

  const updateActiveProspect = useUpdateProspect(
    campaignId,
    activeProspectId ?? 0,
  )
  const createNoteForActive = useCreateProspectNote(
    campaignId,
    activeProspectId ?? 0,
  )

  // Auto-select premier pending (ou premier prospect) à l'arrivée
  useEffect(() => {
    if (activeProspectId !== null) return
    if (prospects.length === 0) return
    const firstPending = prospects.find((p) => p.status === 'pending')
    setActiveProspectId(firstPending?.id ?? prospects[0].id)
  }, [prospects, activeProspectId])

  // Auto-select premier campaign_number quand la campagne charge
  useEffect(() => {
    if (selectedNumber) return
    const first = campaignQuery.data?.campaign_numbers?.[0]
    if (first?.phone_number_details?.phone_number) {
      setSelectedNumber(first.phone_number_details.phone_number)
    }
  }, [campaignQuery.data, selectedNumber])

  const activeProspect = useMemo(
    () => prospects.find((p) => p.id === activeProspectId) || null,
    [prospects, activeProspectId],
  )

  const filteredProspects = useMemo(() => {
    if (filter === 'all') return prospects
    // "À rappeler" : catégorie callback (couvre les dispositions custom dont le
    // slug de status n'est pas littéralement 'callback').
    if (filter === 'callback') {
      return prospects.filter(
        (p) => p.disposition?.category === 'callback' || p.status === 'callback',
      )
    }
    // "Faits" : traités ET terminés — on EXCLUT les rappels programmés (un
    // callback à venir n'est pas "fait").
    if (filter === 'done') {
      return prospects.filter(
        (p) =>
          p.status !== 'pending' &&
          p.status !== 'callback' &&
          p.disposition?.category !== 'callback',
      )
    }
    return prospects.filter((p) => p.status === filter)
  }, [prospects, filter])

  // Reset disposition + callback au changement de prospect
  useEffect(() => {
    setSelectedDispositionSlug(null)
    setCallbackDate(null)
  }, [activeProspectId])

  const handleSkip = () => {
    if (!activeProspect) return
    const idx = filteredProspects.findIndex((p) => p.id === activeProspect.id)
    const next = filteredProspects[idx + 1]
    if (next) setActiveProspectId(next.id)
  }

  const confirmAndNext = async () => {
    if (!activeProspect || !selectedDispositionSlug) return

    // Bloque la confirmation si on choisit "callback" sans avoir saisi une date
    // (filet UI : PdScriptPanel désactive déjà le bouton dans ce cas).
    const selected = dispositionsQuery.data?.dispositions.find(
      (d) => d.slug === selectedDispositionSlug,
    )
    const isCallback = selected?.category === 'callback'
    if (isCallback && !callbackDate) return

    try {
      const patch: UpdateProspectInput = { status: selectedDispositionSlug }
      // Convertit la valeur datetime-local (timezone-naive locale) en ISO
      // UTC pour stockage backend.
      patch.callback_date = isCallback && callbackDate
        ? new Date(callbackDate).toISOString()
        : null

      await updateActiveProspect.mutateAsync(patch)
      // Note: la quick note du PdScriptPanel est gérée par son propre bouton "Sauvegarder",
      // pas par "Confirmer & Suivant" (alignement Next : note séparée du PATCH status).
      void createNoteForActive
      setSelectedDispositionSlug(null)
      setCallbackDate(null)

      // Avancer au prochain pending (dans filteredProspects, après l'index actuel)
      const idx = filteredProspects.findIndex((p) => p.id === activeProspect.id)
      const nextPending = filteredProspects
        .slice(idx + 1)
        .find((p) => p.status === 'pending')
      if (nextPending) {
        setActiveProspectId(nextPending.id)
        return
      }
      // Plus aucun pending dans toute la campagne → marquer completed
      const anyPending = prospects.some(
        (p) => p.id !== activeProspect.id && p.status === 'pending',
      )
      if (!anyPending) {
        await updateCampaign.mutateAsync({ status: 'completed' })
      }
    } catch {
      // toast géré ailleurs
    }
  }

  const isLoading =
    campaignQuery.isLoading || prospectsQuery.isLoading || statsQuery.isLoading

  if (isLoading) {
    return (
      <div
        className={cn(
          'h-full flex items-center justify-center',
          darkMode ? 'bg-slate-950' : 'bg-slate-50',
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    )
  }

  const campaign = campaignQuery.data
  if (!campaign) {
    return (
      <div
        className={cn(
          'h-full flex items-center justify-center',
          darkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-50 text-slate-500',
        )}
      >
        <p className="text-sm">{t('powerDialer.campaignNotFound', 'Campagne introuvable')}</p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'h-full w-full flex flex-col overflow-hidden',
        darkMode ? 'bg-slate-950' : 'bg-slate-50',
      )}
    >
      {/* 3 colonnes principales */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Colonne 1 — Prospects list */}
        <PdProspectsList
          campaignId={campaignId}
          prospects={prospects}
          filteredProspects={filteredProspects}
          activeProspectId={activeProspectId}
          onSelectProspect={setActiveProspectId}
          filter={filter}
          onFilterChange={setFilter}
          stats={statsQuery.data}
          darkMode={darkMode}
        />

        {/* Colonne 2 — Profil prospect */}
        <PdProspectDetail
          campaignId={campaignId}
          activeProspect={activeProspect}
          darkMode={darkMode}
        />

        {/* Colonne 3 — Script + dispositions + notes */}
        <PdScriptPanel
          campaignId={campaignId}
          campaign={campaign}
          activeProspect={activeProspect}
          selectedDispositionSlug={selectedDispositionSlug}
          onSelectDisposition={setSelectedDispositionSlug}
          callbackDate={callbackDate}
          onCallbackDateChange={setCallbackDate}
          onConfirm={() => void confirmAndNext()}
          saving={updateActiveProspect.isPending}
          darkMode={darkMode}
        />
      </div>

      {/* Bottom bar */}
      <PdCallBar
        campaignId={campaignId}
        campaign={campaign}
        activeProspect={activeProspect}
        selectedNumber={selectedNumber}
        onSelectNumber={setSelectedNumber}
        onBack={onBack}
        onSkip={handleSkip}
        onOpenVmManager={() => setVmManagerOpen(true)}
        darkMode={darkMode}
      />

      {/* Modale VM Manager */}
      <PdVmManagerModal
        open={vmManagerOpen}
        onClose={() => setVmManagerOpen(false)}
        darkMode={darkMode}
      />

    </div>
  )
}
